import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  LuNewspaper,
  LuExternalLink,
  LuCalendar,
  LuTrendingUp,
  LuBell,
  LuArrowRight,
  LuRefreshCw,
  LuLinkedin,
  LuTwitter,
  LuRss,
  LuSparkles,
  LuMessageSquare,
  LuFileText,
  LuClipboardCheck,
  LuCreditCard,
  LuFolderOpen,
  LuCalendarDays,
  LuChevronLeft,
  LuChevronRight,
  LuCircleHelp,
  LuClock,
  LuBadgeCheck,
  LuBookOpen
} from 'react-icons/lu';
import { useTheme, useSocket } from '../../context/SocketContext';
import GuidancePopup from '../../components/GuidancePopup';
import { FaEye, FaDownload } from 'react-icons/fa';

// Import Documents
import doc1 from '../../Assets/Quick Guide on USA Business Entities.pdf';
import doc2 from '../../Assets/important update for all business owners and professionals in our community..jpeg';
import doc3 from '../../Assets/Monthly Bookkeping Checklist for Startups.pdf';
import doc4 from '../../Assets/Common Bookkeping Misktakes Founders make.jpeg';
import doc5 from '../../Assets/US-India-Tax-Guide-for-NRIs-H1B-Professionals-and-US-Citizens (2).pdf';

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// Notification type config
const getNotifConfig = (type) => {
  switch (type) {
    case "chat":
      return { icon: LuMessageSquare, borderColor: 'border-blue-200 dark:border-blue-800/50', bgColor: 'bg-blue-50 dark:bg-blue-900/20', iconBg: 'bg-blue-100 dark:bg-blue-900/50', iconColor: 'text-blue-600 dark:text-blue-400' };
    case "invoice":
      return { icon: LuFileText, borderColor: 'border-emerald-200 dark:border-emerald-800/50', bgColor: 'bg-emerald-50 dark:bg-emerald-900/20', iconBg: 'bg-emerald-100 dark:bg-emerald-900/50', iconColor: 'text-emerald-600 dark:text-emerald-400' };
    case "meeting":
      return { icon: LuCalendarDays, borderColor: 'border-purple-200 dark:border-purple-800/50', bgColor: 'bg-purple-50 dark:bg-purple-900/20', iconBg: 'bg-purple-100 dark:bg-purple-900/50', iconColor: 'text-purple-600 dark:text-purple-400' };
    case "checklist":
      return { icon: LuClipboardCheck, borderColor: 'border-amber-200 dark:border-amber-800/50', bgColor: 'bg-amber-50 dark:bg-amber-900/20', iconBg: 'bg-amber-100 dark:bg-amber-900/50', iconColor: 'text-amber-600 dark:text-amber-400' };
    case "payment":
    case "service_purchase":
      return { icon: LuCreditCard, borderColor: 'border-pink-200 dark:border-pink-800/50', bgColor: 'bg-pink-50 dark:bg-pink-900/20', iconBg: 'bg-pink-100 dark:bg-pink-900/50', iconColor: 'text-pink-600 dark:text-pink-400' };
    case "document":
      return { icon: LuFolderOpen, borderColor: 'border-cyan-200 dark:border-cyan-800/50', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', iconBg: 'bg-cyan-100 dark:bg-cyan-900/50', iconColor: 'text-cyan-600 dark:text-cyan-400' };
    default:
      return { icon: LuBell, borderColor: 'border-gray-200 dark:border-dark-600', bgColor: 'bg-gray-50 dark:bg-dark-700', iconBg: 'bg-gray-200 dark:bg-dark-600', iconColor: 'text-gray-600 dark:text-gray-400' };
  }
};

const Dashboard = () => {
  const { currentThemeColors } = useTheme();
  const socket = useSocket();
  const navigate = useNavigate();
  const { setChatboxOpen } = useOutletContext();
  const [blogs, setBlogs] = useState([]);
  const [showGuidance, setShowGuidance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blogPage, setBlogPage] = useState(1);
  const [totalBlogPages, setTotalBlogPages] = useState(1);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [upcomingMeetingsCount, setUpcomingMeetingsCount] = useState(0);

  // Fetch recent blogs
  const fetchBlogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs?page=${page}&limit=4`
      );
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
        setTotalBlogPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(blogPage);
  }, [blogPage, fetchBlogs]);

  // Fetch upcoming meetings
  useEffect(() => {
    const fetchMeetings = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/meetings/user/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const now = new Date();
          const soon = data.filter(m => new Date(m.startTime || m.start_time) > now);
          setUpcomingMeetingsCount(soon.length);
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    };

    fetchMeetings();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUserData();
  }, []);

  // Show guidance popup only once per session
  useEffect(() => {
    const hasSeen = sessionStorage.getItem('hasSeenGuidance');
    if (!hasSeen) {
      const timer = setTimeout(() => {
        setShowGuidance(true);
        sessionStorage.setItem('hasSeenGuidance', 'true');
      }, 1500); // Small delay for better UX
      return () => clearTimeout(timer);
    }
  }, []);

  const scrollToWhatsNew = () => {
    const element = document.getElementById('whats-new-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    if (!user?._id) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/user/${user._id}`
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Socket.io real-time notifications
  useEffect(() => {
    if (!socket || !user?._id) return;

    socket.emit('joinRoom', user._id);

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    socket.on('newNotification', handleNewNotification);

    return () => {
      socket.off('newNotification', handleNewNotification);
    };
  }, [socket, user]);

  // Handle notification click on dashboard
  const handleNotifClick = async (notif) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${notif._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    if (notif.link === '/chat') {
      setChatboxOpen(true);
    } else if (notif.link) {
      navigate(notif.link);
    }
  };

  // Truncate text helper
  const truncateText = (html, maxLength) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const userDisplayName = (() => {
    if (!user) return 'there';
    if (user.firstName) {
      return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
    }
    if (user.fullName) return user.fullName;
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    return 'there';
  })();

  return (
    <div className="space-y-6">
      <GuidancePopup
        isOpen={showGuidance}
        onClose={() => setShowGuidance(false)}
        onOpenWhatNew={scrollToWhatsNew}
      />
      {/* Welcome Header */}
      <div
        className="card p-6 md:p-8 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-2">
              Welcome back, <span style={{ color: 'var(--primary-500)' }}>{userDisplayName}</span>!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Stay updated with the latest news, blog posts, and notifications.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                localStorage.removeItem('hasSeenTaxFilingTour');
                window.location.href = '/tax-filing?startTour=true';
              }}
              className="hidden flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-bold shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-200 active:scale-95 whitespace-nowrap"
            >
              <LuCircleHelp className="w-4 h-4" />
              Start Interactive Tour
            </button>
            <span className="px-4 py-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium">
              <LuCalendar className="inline-block w-4 h-4 mr-2" />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>
      {/* Service Deadlines Card - Full Width between Banner and Content */}
      {user && (user.dueDate || user.annualReportService?.dueDate || user.registeredAgentService?.dueDate || (user.members && user.members.some(m => m.dueDate))) && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Tax Filing Deadlines */}
            {(user.dueDate || (user.members && user.members.some(m => m.dueDate))) && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border min-h-[160px]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase">Tax Filing</h3>
                  <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase">Due Date</span>
                </div>
                <div className="space-y-4 flex-1">
                  {user.dueDate && (
                    <div className="flex flex-col group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{user.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(user.dueDate)}</div>
                          {(() => {
                            const days = Math.ceil((new Date(user.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 && <span className={`text-[10px] font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                          })()}
                        </div>
                      </div>
                      {user.taxFilingLocked ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate('/pricing?service=service_2&step=1.5'); }}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 transition-all active:scale-95 shadow-lg shadow-orange-600/20"
                        >
                          <LuCreditCard className="w-3.5 h-3.5" />
                          Pay Now
                        </button>
                      ) : (
                        <span className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5 w-full border border-emerald-100 dark:border-emerald-800/30">
                          <LuBadgeCheck className="w-3.5 h-3.5" />
                          Activated
                        </span>
                      )}
                    </div>
                  )}
                  {user.members && user.members.filter(m => m.dueDate).map((member, idx) => (
                    <div key={idx} className="flex flex-col pt-5 border-t border-gray-50 dark:border-dark-700 group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[150px]">{member.name}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(member.dueDate)}</div>
                          {(() => {
                            const days = Math.ceil((new Date(member.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                            return days > 0 && <span className={`text-[10px] font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                          })()}
                        </div>
                      </div>
                      {member.taxFilingLocked ? (
                        <button 
                          onClick={(e) => { e.stopPropagation(); navigate('/pricing?service=service_2&step=1.5'); }}
                          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 transition-all active:scale-95 shadow-lg shadow-orange-600/20"
                        >
                          <LuCreditCard className="w-3.5 h-3.5" />
                          Pay Now
                        </button>
                      ) : (
                        <span className="px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5 w-full border border-emerald-100 dark:border-emerald-800/30">
                          <LuBadgeCheck className="w-3.5 h-3.5" />
                          Activated
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {user.annualReportService?.dueDate && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase">Annual Report</h3>
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase">Due Date</span>
                </div>
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[150px]">{user.annualReportService.state} {user.annualReportService.entityType}</span>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {user.annualReportService.dueDateText || formatDate(user.annualReportService.dueDate)}
                    </div>
                    {user.annualReportService.dueDate && (() => {
                      const days = Math.ceil((new Date(user.annualReportService.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                      return days > 0 && <span className={`text-[10px] font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-all active:scale-95 shadow-lg shadow-emerald-600/20"
                >
                  <LuCreditCard className="w-3.5 h-3.5" />
                  Pay Now
                </button>
              </div>
            )}

            {/* Registered Agent Deadline */}
            {user.registeredAgentService?.dueDate && (
              <div className="p-4 rounded-2xl bg-white dark:bg-dark-800 border-none shadow-sm flex flex-col animated-gradient-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">Registered Agent</h3>
                  <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase">Due Date</span>
                </div>
                <div className="flex items-center justify-between flex-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Service Renewal</span>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white">{formatDate(user.registeredAgentService.dueDate)}</div>
                    {(() => {
                      const days = Math.ceil((new Date(user.registeredAgentService.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
                      return days > 0 && <span className={`text-[10px] font-medium ${days <= 30 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`}>{days}d left</span>;
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-600/20"
                >
                  <LuCreditCard className="w-3.5 h-3.5" />
                  Pay Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Blog Posts - Takes 2 columns */}
        <div
          className="lg:col-span-2 card p-6 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, var(--primary-500), var(--primary-600))' }}
              >
                <LuNewspaper className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-dark-800 dark:text-white">Latest Blog Posts</h2>
            </div>
            <a
              href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              View All
              <LuArrowRight className="w-4 h-4" />
            </a>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-32 bg-gray-200 dark:bg-dark-600 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : blogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((blog, index) => (
                <a
                  key={blog._id}
                  href={`${process.env.REACT_APP_MAIN_WEBSITE_URL || 'https://bookscomply.com'}/blog/${blog.slug || blog._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl overflow-hidden border border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  {blog.image && (
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${blog.image}`}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      {blog.category && (
                        <span className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                          {blog.category}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-dark-800 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                      {truncateText(blog.description, 80)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                      <span className="flex items-center gap-1">
                        <LuCalendar className="w-3 h-3" />
                        {formatDate(blog.createdAt)}
                      </span>
                      <LuExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <LuNewspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No blog posts available</p>
            </div>
          )}

          {/* Blog Pagination */}
          {!loading && totalBlogPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-gray-100 dark:border-dark-600">
              <button
                onClick={() => setBlogPage(prev => Math.max(1, prev - 1))}
                disabled={blogPage === 1}
                className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <LuChevronLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-1 sm:gap-2">
                {(() => {
                  const pages = [];
                  const maxVisible = window.innerWidth < 640 ? 3 : 5;
                  let start = Math.max(1, blogPage - Math.floor(maxVisible / 2));
                  let end = Math.min(totalBlogPages, start + maxVisible - 1);

                  if (end - start < maxVisible - 1) {
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  for (let i = start; i <= end; i++) {
                    pages.push(i);
                  }
                  return pages;
                })().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setBlogPage(pageNum)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-xs sm:text-sm font-medium transition-all ${blogPage === pageNum
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700'
                      }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setBlogPage(prev => Math.min(totalBlogPages, prev + 1))}
                disabled={blogPage === totalBlogPages}
                className="p-2 rounded-lg bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <LuChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Right Column - Deadlines & Notifications */}
        <div className="space-y-6">


          {/* Notifications Card */}
          <div
            className="card p-6 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, var(--accent-500), var(--accent-600))' }}
              >
                <LuBell className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-dark-800 dark:text-white">Notifications</h2>
                {notifications.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{notifications.filter(n => !n.isRead).length} unread</p>
                )}
              </div>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
              {notifLoading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse p-4 rounded-xl border border-gray-200 dark:border-dark-600">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-dark-600" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4 mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-full mb-2" />
                        <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))
              ) : notifications.length > 0 ? (
                notifications.slice(0, 10).map((notif) => {
                  const config = getNotifConfig(notif.type);
                  const IconComp = config.icon;
                  return (
                    <div
                      key={notif._id}
                      onClick={() => handleNotifClick(notif)}
                      className={`p-4 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-md cursor-pointer ${config.borderColor} ${config.bgColor}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.iconBg}`}>
                          <IconComp className={`w-4 h-4 ${config.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-dark-800 dark:text-white text-sm mb-1">
                            {notif.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {timeAgo(notif.createdAt)}
                            </span>
                            {!notif.isRead && (
                              <span className="w-2 h-2 rounded-full bg-primary-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                    <LuBell className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">You'll see updates here when there's activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity / Updates Section */}
      <div
        id="whats-new-section"
        className="card p-6 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))' }}
            >
              <LuRefreshCw className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-dark-800 dark:text-white">What's New</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Document 5: US-India Tax Guide for NRIs, H1B Professionals & US Citizens */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-emerald-300 dark:hover:border-emerald-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuBookOpen className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              US-India Tax Guide for NRIs, H1B Professionals & US Citizens
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
              A comprehensive guide on tax liabilities, double taxation, and filing requirements for NRIs and US expats.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              <a
                href={doc5}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 font-bold text-sm hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                <FaEye className="w-4 h-4" />
                View
              </a>
              <a
                href={doc5}
                download="US-India-Tax-Guide-for-NRIs-H1B-Professionals-and-US-Citizens.pdf"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-500/30 transition-all active:scale-95"
              >
                <FaDownload className="w-3 h-3" />
                Download
              </a>
            </div>
          </div>

          {/* Document 3: Monthly Bookkeeping Checklist for Startups */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuClipboardCheck className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              Monthly Bookkeeping Checklist for Startups
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
              Ensure nothing falls through the cracks with this essential monthly checklist for founders.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              <a
                href={doc3}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold text-sm hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                <FaEye className="w-4 h-4" />
                View
              </a>
              <a
                href={doc3}
                download="Monthly_Bookkeeping_Checklist_for_Startups.pdf"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all active:scale-95"
              >
                <FaDownload className="w-3 h-3" />
                Download
              </a>
            </div>
          </div>

          {/* Document 4: Common Bookkeeping Mistakes Founders make */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-purple-300 dark:hover:border-purple-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuFolderOpen className="w-7 h-7 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              Common Bookkeeping Mistakes Founders Make
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
              Learn how to avoid the most common financial pitfalls and keep your books clean.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              <a
                href={doc4}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 font-bold text-sm hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
              >
                <FaEye className="w-4 h-4" />
                View
              </a>
              <a
                href={doc4}
                download="Common_Bookkeeping_Mistakes.jpeg"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-600 text-white font-bold text-sm hover:bg-purple-700 shadow-lg shadow-purple-500/30 transition-all active:scale-95"
              >
                <FaDownload className="w-3 h-3" />
                Download
              </a>
            </div>
          </div>

          {/* Document 1: Quick Guide on USA Business Entities */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuFileText className="w-7 h-7 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              Quick Guide on USA Business Entities
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              A comprehensive guide to help you choose the right legal structure for your US business.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              <a
                href={doc1}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-bold text-sm hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
              >
                <FaEye className="w-4 h-4" />
                View
              </a>
              <a
                href={doc1}
                download="Quick_Guide_USA_Business_Entities.pdf"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-600 text-white font-bold text-sm hover:bg-primary-700 shadow-lg shadow-primary-500/30 transition-all active:scale-95"
              >
                <FaDownload className="w-3 h-3" />
                Download
              </a>
            </div>
          </div>

          {/* Document 2: Important update for business owners */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-secondary-300 dark:hover:border-secondary-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-secondary-50 dark:bg-secondary-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuSparkles className="w-7 h-7 text-secondary-600 dark:text-secondary-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              Mid-Year Tax Check: Are You Overpaying US Taxes?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
              Stay ahead of your taxes and identify potential savings with our mid-year evaluation.
            </p>
            <div className="flex items-center gap-3 mt-auto">
              <a
                href={doc2}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-secondary-50 dark:bg-secondary-900/20 text-secondary-600 dark:text-secondary-400 font-bold text-sm hover:bg-secondary-100 dark:hover:bg-secondary-900/40 transition-colors"
              >
                <FaEye className="w-4 h-4" />
                View
              </a>
              <a
                href={doc2}
                download="Community_Update.jpeg"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-secondary-600 text-white font-bold text-sm hover:bg-secondary-700 shadow-lg shadow-secondary-500/30 transition-all active:scale-95"
              >
                <FaDownload className="w-3 h-3" />
                Download
              </a>
            </div>
          </div>

          {/* Help Guide (Keeping one of the original) */}
          <div className="group relative p-6 rounded-2xl bg-white dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-amber-300 dark:hover:border-amber-500 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col h-full">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <LuCircleHelp className="w-7 h-7 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="font-bold text-lg text-dark-800 dark:text-white mb-2 leading-tight">
              Detailed Help Guide
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Need assistance? Explore our comprehensive help guide for step-by-step instructions.
            </p>
            <Link
              to="/help"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 shadow-lg shadow-amber-500/30 transition-all active:scale-95 mt-auto"
            >
              Get Help <LuArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <div
        className="card p-6 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: 'linear-gradient(135deg, var(--secondary-500), var(--secondary-600))' }}
          >
            <LuRss className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">Connect With Us</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LinkedIn Section */}
          <div className="rounded-xl border border-gray-200 dark:border-dark-600 overflow-hidden">
            <div className="p-4 bg-[#0077B5]/10 dark:bg-[#0077B5]/20 border-b border-gray-200 dark:border-dark-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0077B5] flex items-center justify-center">
                  <LuLinkedin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-800 dark:text-white">LinkedIn</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Follow us for updates</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Stay connected with BooksComply on LinkedIn for industry insights, company updates, and professional networking opportunities.
              </p>
              <a
                href="https://www.linkedin.com/company/bookscomply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0077B5] text-white text-sm font-medium hover:bg-[#006699] transition-colors"
              >
                <LuLinkedin className="w-4 h-4" />
                Follow on LinkedIn
                <LuExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Twitter/X Section */}
          <div className="rounded-xl border border-gray-200 dark:border-dark-600 overflow-hidden">
            <div className="p-4 bg-black/5 dark:bg-white/10 border-b border-gray-200 dark:border-dark-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black dark:bg-white flex items-center justify-center">
                  <LuTwitter className="w-5 h-5 text-white dark:text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-800 dark:text-white">Twitter / X</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Latest tweets & updates</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Follow @BooksComply on Twitter for quick tips, tax updates, and engaging content about financial compliance.
              </p>
              <a
                href="https://twitter.com/bookscomply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <LuTwitter className="w-4 h-4" />
                Follow on X
                <LuExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
